import React, { useState, useEffect } from "react";
import { enGB } from 'date-fns/locale';
import { 
  Typography, 
  Grid, 
  Button, 
  Chip, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField,
  Box,
  CircularProgress,
  Divider,
  Container,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  EventAvailable, 
  Sick, 
  Warning, 
  Cancel, 
  DateRange, 
  ArrowBack,
  NavigateNext,
  Check,
  Home
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, differenceInDays, isBefore, isAfter } from 'date-fns';
import { applyForLeave } from '../../../../Action/api';
import { useNavigate, useLocation } from 'react-router-dom';

const LeaveApplyForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from route state
  const { leaveTypes = [], leaveBalance = {}, leaveHistory = [] } = location.state || {};

  // State for success/error messages
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);

  // State for form fields
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [leaveType, setLeaveType] = useState('');
  const [reason, setReason] = useState('');
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isUsingDefaults, setIsUsingDefaults] = useState(true);
  const [conflictingTasks, setConflictingTasks] = useState([]);
  const [taskConflictMessage, setTaskConflictMessage] = useState("");
  const [showTaskConflict, setShowTaskConflict] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Define steps for the form process
  const steps = ['Select Leave Type', 'Choose Dates', 'Provide Details', 'Review & Submit'];

  // Initialize form when component mounts
  useEffect(() => {
    initializeForm();
  }, []);

  // Check if we have the necessary data
  useEffect(() => {
    // If leaveBalance is empty, navigate back to the main page
    if (!leaveBalance.annual || !leaveBalance.sick || !leaveBalance.emergency) {
      setErrorMessage("Required data is missing. Please try again.");
      setOpenError(true);
      // Wait a moment to show the error before navigating
      setTimeout(() => {
        navigate('/my-leave');
      }, 2000);
    }
  }, [leaveBalance, navigate]);

  // Initialize form with default values
  const initializeForm = () => {
    // Set default type to Annual Leave
    setLeaveType('Annual Leave');
    
    // Set default start date to current date
    const today = new Date();
    setStartDate(today);
    setEndDate(today);
    
    // Set default reason for Annual Leave
    setReason("Planned family vacation that was scheduled in advance.");
    
    // Calculate days
    setCalculatedDays(1);
    
    // Set flag that we're using default values
    setIsUsingDefaults(true);
    
    // Reset task conflict
    setConflictingTasks([]);
    setTaskConflictMessage("");
    setShowTaskConflict(false);
  };
  
  // Handle going back to previous page
  const handleBack = () => {
    navigate('/my-leave');
  };
  
  // Modify handleLeaveTypeChange to update isUsingDefaults
  const handleLeaveTypeChange = (e) => {
    const selectedType = e.target.value;
    setLeaveType(selectedType);
    
    // Set default reason based on leave type
    if (selectedType === 'Annual Leave') {
      setReason("Planned family vacation that was scheduled in advance.");
    } else if (selectedType === 'Sick Leave') {
      setReason("Not feeling well due to a viral infection. Doctor has advised rest for a day.");
    } else if (selectedType === 'Emergency Leave') {
      setReason("An unexpected family situation has arisen and requires my urgent attention.");
    } else {
      setReason("");
    }
    
    // Set to false only if user selected something other than default
    if (selectedType !== 'Annual Leave') {
      setIsUsingDefaults(false);
    }
  };
  
  // Handle date changes
  const handleDateChange = (field, newValue) => {
    // First, determine if we're modifying default values
    const today = new Date();
    const isToday = newValue && 
      newValue.getDate() === today.getDate() && 
      newValue.getMonth() === today.getMonth() && 
      newValue.getFullYear() === today.getFullYear();
      
    if (!isToday) {
      setIsUsingDefaults(false);
    }

    if (field === 'start') {
      setStartDate(newValue);
      
      // If end date exists but is before the new start date, update end date to match start date
      if (endDate && isBefore(endDate, newValue)) {
        setEndDate(newValue);
      }
    } else {
      // Only allow end dates that are on or after the start date
      if (startDate && isBefore(newValue, startDate)) {
        // If user tries to select an end date before start date, set it equal to start date
        setEndDate(startDate);
      } else {
        setEndDate(newValue);
      }
    }
  };
  
  // Handle reason changes
  const handleReasonChange = (e) => {
    const newReason = e.target.value;
    setReason(newReason);
    
    // Check if the value matches any of our defaults
    const annualDefault = "Planned family vacation that was scheduled in advance.";
    const sickDefault = "Not feeling well due to a viral infection. Doctor has advised rest for a day.";
    const emergencyDefault = "An unexpected family situation has arisen and requires my urgent attention.";
    
    if (
      newReason !== annualDefault && 
      newReason !== sickDefault && 
      newReason !== emergencyDefault
    ) {
      setIsUsingDefaults(false);
    }
  };
  
  // Calculate days between start and end date
  useEffect(() => {
    if (startDate && endDate) {
      if (isBefore(endDate, startDate)) {
        setCalculatedDays(0);
      } else {
        // Add 1 to include both start and end days
        setCalculatedDays(differenceInDays(endDate, startDate) + 1);
      }
    } else {
      setCalculatedDays(0);
    }
  }, [startDate, endDate]);
  
  // Check if user has enough leave balance
  const hasEnoughBalance = () => {
    if (!leaveType || !startDate || !endDate || !leaveBalance) return true;
    
    const days = calculatedDays;
    
    if (leaveType === 'Annual Leave' && leaveBalance.annual) {
      return days <= leaveBalance.annual.remaining;
    } else if (leaveType === 'Sick Leave' && leaveBalance.sick) {
      return days <= leaveBalance.sick.remaining;
    } else if (leaveType === 'Emergency Leave' && leaveBalance.emergency) {
      return days <= leaveBalance.emergency.remaining;
    }
    
    return true;
  };
  
  // Check if selected dates overlap with existing approved leave applications
  const hasDateOverlap = () => {
    if (!startDate || !endDate || !leaveHistory) return false;
    
    // Only check for Approved leaves
    const conflictingLeaves = leaveHistory.filter(leave => 
      leave.status === 'Approved' &&
      ((isAfter(new Date(leave.startDate), startDate) && isBefore(new Date(leave.startDate), endDate)) ||
       (isAfter(new Date(leave.endDate), startDate) && isBefore(new Date(leave.endDate), endDate)) ||
       (isBefore(new Date(leave.startDate), startDate) && isAfter(new Date(leave.endDate), endDate)) ||
       (format(new Date(leave.startDate), 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd')) ||
       (format(new Date(leave.endDate), 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')))
    );
    
    return conflictingLeaves.length > 0;
  };
  
  // Function to get details of overlapping leaves for display
  const getOverlappingLeaveDetails = () => {
    if (!startDate || !endDate || !leaveHistory) return [];
    
    return leaveHistory.filter(leave => 
      leave.status === 'Approved' &&
      ((isAfter(new Date(leave.startDate), startDate) && isBefore(new Date(leave.startDate), endDate)) ||
       (isAfter(new Date(leave.endDate), startDate) && isBefore(new Date(leave.endDate), endDate)) ||
       (isBefore(new Date(leave.startDate), startDate) && isAfter(new Date(leave.endDate), endDate)) ||
       (format(new Date(leave.startDate), 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd')) ||
       (format(new Date(leave.endDate), 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')))
    );
  };

  // Handle submit
  const handleSubmitLeave = async () => {
    if (!leaveType || !startDate || !endDate || !reason) {
      setErrorMessage("Please fill all required fields");
      setOpenError(true);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get user ID from localStorage
      const userData = JSON.parse(localStorage.getItem('userData')) || {};
      const userId = userData.userId || '';
      
      if (!userId) {
        setErrorMessage("User ID not found. Please log out and log in again to refresh your session.");
        setOpenError(true);
        setIsLoading(false);
        return;
      }
      
      // Find the leave code based on type
      const selectedLeaveType = leaveTypes.find(leave => leave.leaveName === leaveType);
      const leaveCode = selectedLeaveType ? selectedLeaveType.leaveCode : '';
      
      // Prepare leave application data
      const leaveData = {
        userId: userId,
        leaveType: leaveType,
        leaveCode: leaveCode,
        leaveStartDate: format(startDate, 'yyyy-MM-dd'),
        leaveEndDate: format(endDate, 'yyyy-MM-dd'),
        leaveReason: reason,
        leaveAppliedOn: format(new Date(), 'yyyy-MM-dd'),
        leaveStatus: 'Pending'
      };
      
      const response = await applyForLeave(leaveData);
      
      // Check if response contains task conflicts
      if (response && response.status === false && response.conflictingTasks) {
        // Set state to show task conflicts in the dialog
        setConflictingTasks(response.conflictingTasks);
        setTaskConflictMessage(response.uiMsg || "There are tasks scheduled during your leave period.");
        setShowTaskConflict(true);
        
        // Don't navigate away since we want to show the conflict message
      } else if (response && response.status) {
        // Success case
        setErrorMessage("Leave application submitted successfully!");
        setOpenSuccess(true);
        
        // Navigate back to the leave management page after a short delay
        setTimeout(() => {
          navigate('/my-leave', { 
            state: { 
              successMessage: "Leave application submitted successfully!",
              refreshData: true 
            }
          });
        }, 1500);
      } else {
        // Generic error case
        setErrorMessage("Failed to submit leave application. Please try again.");
        setOpenError(true);
      }
    } catch (error) {
      console.error('Error applying for leave:', error);
      setErrorMessage("Failed to submit leave application. Please try again.");
      setOpenError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      py: 5, 
      px: { xs: 2, sm: 3, md: 4 },
      background: 'linear-gradient(to bottom, #f5f7fa, #ffffff)',
      minHeight: '100vh'
    }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3, color: 'text.secondary' }}
      >
        <Link 
          underline="hover" 
          color="inherit" 
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => navigate('/task-dashboard')}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/my-leave')}
        >
          Leave Management
        </Link>
        <Typography color="text.primary">Apply for Leave</Typography>
      </Breadcrumbs>
      
      {/* Page Title */}
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          fontWeight: 700, 
          color: 'primary.dark',
          mb: 4
        }}
      >
        Apply for Leave
      </Typography>
      
      {/* Progress Stepper */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          boxShadow: '0 0 20px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ 
          p: 2, 
          background: 'linear-gradient(to right, #f5f7fa, #e4e7eb)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel
            sx={{ 
              '& .MuiStepLabel-label': { 
                mt: 1,
                fontWeight: 500
              },
              '& .MuiStepLabel-labelContainer': {
                width: { xs: '75px', sm: '100px', md: '120px' }
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Card>
      
      {/* Warning alerts */}
      {isUsingDefaults && (
        <Card 
          elevation={0} 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            border: '1px solid #FFD54F',
            bgcolor: 'rgba(255, 213, 79, 0.1)',
            boxShadow: '0 0 20px rgba(255, 213, 79, 0.15)'
          }}
        >
          <CardContent sx={{ 
            p: 3,
            display: 'flex',
            alignItems: 'flex-start'
          }}>
            <Warning sx={{ 
              color: 'warning.main', 
              mr: 2, 
              fontSize: 30,
              mt: 0.5
            }} />
            <Box>
              <Typography variant="h6" sx={{ 
                color: 'warning.dark', 
                fontWeight: 600, 
                mb: 1 
              }}>
                Sample Values Detected
              </Typography>
              <Typography variant="body1" sx={{ color: 'warning.dark' }}>
                We've pre-filled the form with sample values to help you get started. 
                Please modify these values according to your actual leave requirements before submitting.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {hasDateOverlap() && (
        <Card 
          elevation={0} 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            border: '1px solid #EF5350',
            bgcolor: 'rgba(239, 83, 80, 0.08)',
            boxShadow: '0 0 20px rgba(239, 83, 80, 0.15)'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Cancel sx={{ 
                color: 'error.main', 
                mr: 2, 
                fontSize: 30,
                mt: 0.5
              }} />
              <Box>
                <Typography variant="h6" sx={{ 
                  color: 'error.dark', 
                  fontWeight: 600, 
                  mb: 1 
                }}>
                  Date Conflict Detected
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'error.dark', 
                  fontWeight: 500,
                  mb: 2
                }}>
                  You already have approved leave during this period.
                  Please select different dates to avoid conflicts with your existing approved leave:
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ 
              ml: 6,
              p: 2, 
              bgcolor: 'rgba(211, 47, 47, 0.04)', 
              borderRadius: 2,
              border: '1px solid rgba(211, 47, 47, 0.2)'
            }}>
              {getOverlappingLeaveDetails().map((leave, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    mt: index > 0 ? 2 : 0, 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                >
                  <DateRange fontSize="small" sx={{ mr: 2, color: 'error.main' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'error.main' }}>
                      {format(new Date(leave.startDate), 'dd MMM yyyy')}
                      {leave.endDate !== leave.startDate && 
                        ` - ${format(new Date(leave.endDate), 'dd MMM yyyy')}`} 
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {leave.days} {leave.days === 1 ? 'day' : 'days'} - {leave.type}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
            
      {/* Task conflict message */}
      {showTaskConflict && (
        <Card 
          elevation={0} 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            border: '1px solid #7E57C2',
            bgcolor: 'rgba(126, 87, 194, 0.05)',
            boxShadow: '0 0 20px rgba(126, 87, 194, 0.15)'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Warning sx={{ 
                color: '#5E35B1', 
                mr: 2, 
                fontSize: 30,
                mt: 0.5
              }} />
              <Box>
                <Typography variant="h6" sx={{ 
                  color: '#4527A0', 
                  fontWeight: 600, 
                  mb: 1 
                }}>
                  Task Conflict Detected
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#4527A0', 
                  fontWeight: 500,
                  mb: 2
                }}>
                  {taskConflictMessage}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ 
              ml: 6,
              p: 0,
            }}>
              <Typography variant="subtitle2" sx={{ 
                color: '#4527A0', 
                mb: 2,
                fontWeight: 500
              }}>
                Conflicting Tasks:
              </Typography>
              
              <Grid container spacing={2}>
                {conflictingTasks.map((task, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card variant="outlined" sx={{ 
                      p: 2,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      borderColor: 'rgba(126, 87, 194, 0.3)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ color: '#512DA8', fontWeight: 600 }}>
                          {task.Title}
                        </Typography>
                        <Chip 
                          label={task.Status} 
                          size="small" 
                          color={task.Status === 'To Do' ? 'primary' : 'default'} 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                        Task ID: {task.TaskID}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary', 
                        mb: 2,
                        flexGrow: 1
                      }}>
                        {task.Description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip 
                          label={`Due: ${format(new Date(task.DueDate), 'dd MMM yyyy')}`} 
                          size="small" 
                          icon={<DateRange fontSize="small" />}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                        <Chip 
                          label={task.Priority} 
                          size="small" 
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                        <Chip 
                          label={task.TaskType} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ 
                mt: 3, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                bgcolor: 'rgba(126, 87, 194, 0.1)',
                p: 2,
                borderRadius: 2
              }}>
                <Typography variant="body2" sx={{ color: '#4527A0', fontStyle: 'italic' }}>
                  Please contact your manager before proceeding with this leave request.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  size="small"
                  onClick={() => setShowTaskConflict(false)}
                >
                  Dismiss
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Main Form Section */}
      <Grid container spacing={4}>
        {/* Left Column - Form Fields */}
        <Grid item xs={12} md={8}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.05)',
              height: '100%'
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 3,
                  color: 'primary.main'
                }}
              >
                Leave Request Details
              </Typography>
              
              <Grid container spacing={3}>
                {/* Leave Type Selection */}
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1.5, 
                      color: 'text.primary',
                      fontWeight: 500
                    }}
                  >
                    Leave Type *
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {/* Annual Leave Option Card */}
                    <Grid item xs={12} sm={4}>
                      <Card 
                        variant={leaveType === 'Annual Leave' ? 'outlined' : 'elevation'}
                        elevation={0}
                        onClick={() => handleLeaveTypeChange({ target: { value: 'Annual Leave' } })}
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: leaveType === 'Annual Leave' 
                            ? '2px solid' 
                            : '1px solid rgba(0, 0, 0, 0.12)',
                          borderColor: leaveType === 'Annual Leave' 
                            ? 'primary.main' 
                            : 'divider',
                          borderRadius: 2,
                          bgcolor: leaveType === 'Annual Leave' 
                            ? 'primary.lighter' 
                            : 'background.paper',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)'
                          },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          minHeight: 120
                        }}
                      >
                        {leaveType === 'Annual Leave' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8,
                              bgcolor: 'primary.main',
                              color: 'white',
                              borderRadius: '50%',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Check sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        <EventAvailable 
                          color="primary" 
                          sx={{ 
                            fontSize: 40, 
                            mb: 1.5,
                            color: leaveType === 'Annual Leave' 
                              ? 'primary.main' 
                              : 'action.active'
                          }} 
                        />
                        <Typography 
                          variant="body1" 
                          align="center"
                          sx={{ 
                            fontWeight: leaveType === 'Annual Leave' ? 600 : 500,
                            color: leaveType === 'Annual Leave' 
                              ? 'primary.main' 
                              : 'text.primary'
                          }}
                        >
                          Annual Leave
                        </Typography>
                      </Card>
                    </Grid>
                    
                    {/* Sick Leave Option Card */}
                    <Grid item xs={12} sm={4}>
                      <Card 
                        variant={leaveType === 'Sick Leave' ? 'outlined' : 'elevation'}
                        elevation={0}
                        onClick={() => handleLeaveTypeChange({ target: { value: 'Sick Leave' } })}
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: leaveType === 'Sick Leave' 
                            ? '2px solid' 
                            : '1px solid rgba(0, 0, 0, 0.12)',
                          borderColor: leaveType === 'Sick Leave' 
                            ? 'error.main' 
                            : 'divider',
                          borderRadius: 2,
                          bgcolor: leaveType === 'Sick Leave' 
                            ? 'error.lighter' 
                            : 'background.paper',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: 'error.main',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)'
                          },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          minHeight: 120
                        }}
                      >
                        {leaveType === 'Sick Leave' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8,
                              bgcolor: 'error.main',
                              color: 'white',
                              borderRadius: '50%',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Check sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        <Sick 
                          color="error" 
                          sx={{ 
                            fontSize: 40, 
                            mb: 1.5,
                            color: leaveType === 'Sick Leave' 
                              ? 'error.main' 
                              : 'action.active'
                          }} 
                        />
                        <Typography 
                          variant="body1" 
                          align="center"
                          sx={{ 
                            fontWeight: leaveType === 'Sick Leave' ? 600 : 500,
                            color: leaveType === 'Sick Leave' 
                              ? 'error.main' 
                              : 'text.primary'
                          }}
                        >
                          Sick Leave
                        </Typography>
                      </Card>
                    </Grid>
                    
                    {/* Emergency Leave Option Card */}
                    <Grid item xs={12} sm={4}>
                      <Card 
                        variant={leaveType === 'Emergency Leave' ? 'outlined' : 'elevation'}
                        elevation={0}
                        onClick={() => handleLeaveTypeChange({ target: { value: 'Emergency Leave' } })}
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: leaveType === 'Emergency Leave' 
                            ? '2px solid' 
                            : '1px solid rgba(0, 0, 0, 0.12)',
                          borderColor: leaveType === 'Emergency Leave' 
                            ? 'warning.main' 
                            : 'divider',
                          borderRadius: 2,
                          bgcolor: leaveType === 'Emergency Leave' 
                            ? 'warning.lighter' 
                            : 'background.paper',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: 'warning.main',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)'
                          },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          minHeight: 120
                        }}
                      >
                        {leaveType === 'Emergency Leave' && (
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8,
                              bgcolor: 'warning.main',
                              color: 'white',
                              borderRadius: '50%',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Check sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        <Warning 
                          color="warning" 
                          sx={{ 
                            fontSize: 40, 
                            mb: 1.5,
                            color: leaveType === 'Emergency Leave' 
                              ? 'warning.main' 
                              : 'action.active'
                          }} 
                        />
                        <Typography 
                          variant="body1" 
                          align="center"
                          sx={{ 
                            fontWeight: leaveType === 'Emergency Leave' ? 600 : 500,
                            color: leaveType === 'Emergency Leave' 
                              ? 'warning.main' 
                              : 'text.primary'
                          }}
                        >
                          Emergency Leave
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Date Selection */}
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1.5, 
                      color: 'text.primary',
                      fontWeight: 500
                    }}
                  >
                    Leave Period *
                  </Typography>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      p: 3, 
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                          <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => handleDateChange('start', newValue)}
                            inputFormat="dd/MM/yyyy"
                            renderInput={(params) => (
                              <TextField 
                                {...params} 
                                fullWidth 
                                variant="outlined"
                                InputProps={{
                                  ...params.InputProps,
                                  sx: { borderRadius: 2 }
                                }}
                                helperText="First day of leave"
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                          <DatePicker
                            label="End Date"
                            value={endDate}
                            minDate={startDate || undefined}
                            onChange={(newValue) => handleDateChange('end', newValue)}
                            inputFormat="dd/MM/yyyy"
                            renderInput={(params) => (
                              <TextField 
                                {...params} 
                                fullWidth 
                                variant="outlined"
                                InputProps={{
                                  ...params.InputProps,
                                  sx: { borderRadius: 2 }
                                }}
                                helperText="Last day of leave"
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                    
                    {calculatedDays > 0 && (
                      <Box sx={{ 
                        mt: 3, 
                        p: 2, 
                        bgcolor: 'primary.lighter',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Typography variant="body1" color="primary.dark">
                          Total Duration:
                        </Typography>
                        <Typography variant="h6" color="primary.main" fontWeight="bold">
                          {calculatedDays} {calculatedDays === 1 ? 'day' : 'days'}
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>
                
                {/* Reason */}
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1.5, 
                      color: 'text.primary',
                      fontWeight: 500
                    }}
                  >
                    Reason for Leave *
                  </Typography>
                  <TextField
                    fullWidth
                    id="leave-reason"
                    multiline
                    rows={5}
                    value={reason}
                    onChange={handleReasonChange}
                    placeholder="Please provide a detailed reason for your leave request"
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    helperText="Provide enough details to help your manager understand your leave request"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right Column - Summary */}
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.05)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ 
              p: { xs: 2, sm: 3, md: 4 },
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 4,
                  color: 'primary.main'
                }}
              >
                Leave Summary
              </Typography>
              
              {calculatedDays > 0 && leaveType ? (
                <>
                  {/* Duration Circle */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    mb: 4
                  }}>
                    <Box sx={{ 
                      position: 'relative',
                      width: 140,
                      height: 140,
                      borderRadius: '50%',
                      background: `conic-gradient(
                        ${
                          !hasEnoughBalance() 
                            ? '#f44336' 
                            : leaveType === 'Annual Leave' 
                              ? '#2196f3' 
                              : leaveType === 'Sick Leave' 
                                ? '#f44336' 
                                : '#ff9800'
                        } ${Math.min(calculatedDays / 
                          (leaveType === 'Annual Leave' 
                            ? leaveBalance.annual?.total 
                            : leaveType === 'Sick Leave' 
                              ? leaveBalance.sick?.total 
                              : leaveBalance.emergency?.total) * 360, 360)}deg, 
                        rgba(0, 0, 0, 0.05) 0
                      )`,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                    }}>
                      <Box sx={{ 
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                        <Typography variant="h3" fontWeight="bold" lineHeight={1.2}>
                          {calculatedDays}
                        </Typography>
                        <Typography variant="body2">
                          {calculatedDays === 1 ? 'DAY' : 'DAYS'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Selected Period */}
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      p: 2.5, 
                      mb: 3,
                      borderRadius: 2,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      boxShadow: 'none'
                    }}
                  >
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        fontWeight: 500
                      }}
                    >
                      Selected Period
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <DateRange sx={{ 
                        mr: 1.5, 
                        mt: 0.5, 
                        color: 
                          leaveType === 'Annual Leave' 
                            ? 'primary.main' 
                            : leaveType === 'Sick Leave' 
                              ? 'error.main' 
                              : 'warning.main'
                      }} />
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {startDate && format(startDate, 'dd MMMM yyyy')}
                        </Typography>
                        {startDate && endDate && !isBefore(endDate, startDate) && startDate.getTime() !== endDate.getTime() && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Box sx={{ 
                              width: 20, 
                              borderTop: '1px dashed',
                              borderColor: 'divider',
                              mr: 1
                            }} />
                            <Typography variant="body1" fontWeight={500}>
                              {format(endDate, 'dd MMMM yyyy')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Card>
                  
                  {/* Leave Balance */}
                  {leaveType && leaveBalance && (
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 2,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        boxShadow: 'none',
                        mb: 'auto'
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          fontWeight: 500
                        }}
                      >
                        Leave Balance
                      </Typography>
                      
                      {leaveType === 'Annual Leave' && leaveBalance.annual && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                              Current balance
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" 
                              color={!hasEnoughBalance() ? 'error.main' : 'primary.main'}>
                              {leaveBalance.annual.remaining} days
                            </Typography>
                          </Box>
                          
                          <Box sx={{ 
                            my: 1.5, 
                            height: 4, 
                            bgcolor: 'grey.100', 
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              height: '100%', 
                              width: `${(leaveBalance.annual.remaining / leaveBalance.annual.total) * 100}%`,
                              bgcolor: 'primary.main',
                              borderRadius: 2
                            }} />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Used: {leaveBalance.annual.used} days
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total: {leaveBalance.annual.total} days
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                              This request
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {calculatedDays} days
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                              Remaining after approval
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" 
                              color={!hasEnoughBalance() ? 'error.main' : 'success.main'}>
                              {Math.max(0, leaveBalance.annual.remaining - calculatedDays)} days
                            </Typography>
                          </Box>
                          
                          {!hasEnoughBalance() && (
                            <Box sx={{ 
                              mt: 2, 
                              p: 2,
                              bgcolor: 'error.lighter',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Cancel fontSize="small" color="error" sx={{ mr: 1.5 }} />
                              <Typography variant="body2" color="error.main" fontWeight="medium">
                                Insufficient leave balance
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Similar blocks for Sick Leave and Emergency Leave */}
                      {leaveType === 'Sick Leave' && leaveBalance.sick && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                              Current balance
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" 
                              color={!hasEnoughBalance() ? 'error.main' : 'error.main'}>
                              {leaveBalance.sick.remaining} days
                            </Typography>
                          </Box>
                          
                          <Box sx={{ 
                            my: 1.5, 
                            height: 4, 
                            bgcolor: 'grey.100', 
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              height: '100%', 
                              width: `${(leaveBalance.sick.remaining / leaveBalance.sick.total) * 100}%`,
                              bgcolor: 'error.main',
                              borderRadius: 2
                            }} />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Used: {leaveBalance.sick.used} days
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total: {leaveBalance.sick.total} days
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                              This request
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {calculatedDays} days
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                              Remaining after approval
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" 
                              color={!hasEnoughBalance() ? 'error.main' : 'success.main'}>
                              {Math.max(0, leaveBalance.sick.remaining - calculatedDays)} days
                            </Typography>
                          </Box>
                          
                          {!hasEnoughBalance() && (
                            <Box sx={{ 
                              mt: 2, 
                              p: 2,
                              bgcolor: 'error.lighter',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Cancel fontSize="small" color="error" sx={{ mr: 1.5 }} />
                              <Typography variant="body2" color="error.main" fontWeight="medium">
                                Insufficient leave balance
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {leaveType === 'Emergency Leave' && leaveBalance.emergency && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                              Current balance
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" 
                              color={!hasEnoughBalance() ? 'error.main' : 'warning.main'}>
                              {leaveBalance.emergency.remaining} days
                            </Typography>
                          </Box>
                          
                          <Box sx={{ 
                            my: 1.5, 
                            height: 4, 
                            bgcolor: 'grey.100', 
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              height: '100%', 
                              width: `${(leaveBalance.emergency.remaining / leaveBalance.emergency.total) * 100}%`,
                              bgcolor: 'warning.main',
                              borderRadius: 2
                            }} />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Used: {leaveBalance.emergency.used} days
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total: {leaveBalance.emergency.total} days
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                              This request
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {calculatedDays} days
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                              Remaining after approval
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" 
                              color={!hasEnoughBalance() ? 'error.main' : 'success.main'}>
                              {Math.max(0, leaveBalance.emergency.remaining - calculatedDays)} days
                            </Typography>
                          </Box>
                          
                          {!hasEnoughBalance() && (
                            <Box sx={{ 
                              mt: 2, 
                              p: 2,
                              bgcolor: 'error.lighter',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Cancel fontSize="small" color="error" sx={{ mr: 1.5 }} />
                              <Typography variant="body2" color="error.main" fontWeight="medium">
                                Insufficient leave balance
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Card>
                  )}
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  height: '100%',
                  p: 4,
                  color: 'text.secondary'
                }}>
                  <DateRange sx={{ fontSize: 80, color: 'action.disabled', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="text.secondary">
                    No data to display
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select a leave type and dates to see your leave summary
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mt: 4,
        borderTop: '1px solid',
        borderColor: 'divider',
        pt: 3
      }}>
        <Button 
          variant="outlined" 
          color="inherit" 
          onClick={handleBack}
          startIcon={<ArrowBack />}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          Cancel and Return
        </Button>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmitLeave}
          disabled={
            !leaveType || 
            !startDate || 
            !endDate || 
            !reason || 
            !hasEnoughBalance() || 
            isLoading || 
            isUsingDefaults ||
            hasDateOverlap() ||
            showTaskConflict
          }
          sx={{ 
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <>Submit Leave Request</>
          )}
        </Button>
      </Box>
      
      {/* Error Snackbar */}
      <Snackbar 
        open={openError} 
        autoHideDuration={6000} 
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenError(false)} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
        >
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar 
        open={openSuccess} 
        autoHideDuration={4000} 
        onClose={() => setOpenSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSuccess(false)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
        >
          <AlertTitle>Success</AlertTitle>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LeaveApplyForm;